import type { ReactNode } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import {
  ErrorBoundary as ReactErrorBoundary,
  type FallbackProps,
} from 'react-error-boundary';

import { ErrorFallback } from '../ErrorFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
  onError?: (
    error: unknown,
    errorInfo: { componentStack?: string | null },
  ) => void;
}

/**
 * Error Boundary component to catch React rendering errors
 * Uses react-error-boundary library for better error handling
 * Logs errors to Datadog and provides fallback UI
 *
 * Note: Error boundaries do NOT catch errors in:
 * - Event handlers (use try/catch)
 * - Asynchronous code (use try/catch or error handlers)
 * - Server-side rendering
 * - Errors thrown in the error boundary itself
 */
export const ErrorBoundary = ({ children, onError }: ErrorBoundaryProps) => {
  const handleError = (
    error: unknown,
    errorInfo: { componentStack?: string | null },
  ) => {
    // Log to console for development
    console.error('Error Boundary caught an error:', error, errorInfo);

    // Log error to Datadog
    if (import.meta.env.VITE_DATADOG_CLIENT_TOKEN) {
      const err = error instanceof Error ? error : undefined;
      datadogLogs.logger.error('React Error Boundary - Uncaught Error', {
        message: err?.message,
        stack: err?.stack,
        componentStack: errorInfo.componentStack,
      });
    }

    // Call optional onError callback
    if (onError) {
      onError(error, errorInfo);
    }
  };

  const fallbackRender = ({ error, resetErrorBoundary }: FallbackProps) => {
    return (
      <ErrorFallback
        error={error instanceof Error ? error : null}
        resetErrorBoundary={resetErrorBoundary}
      />
    );
  };

  return (
    <ReactErrorBoundary fallbackRender={fallbackRender} onError={handleError}>
      {children}
    </ReactErrorBoundary>
  );
};
